<script setup lang="ts">
    import { computed, toRef } from 'vue'
    import { RevenueEditorFormSubmitEventPayload } from '@Quest/Revenues/Components/RevenueEditor'
    import type { RevenueEditorViewModel } from '@Quest/Revenues/Components/RevenueEditor'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: RevenueEditorViewModel
    }>()

    const emit = defineEmits<{
        (e: 'form-submit', payload: RevenueEditorFormSubmitEventPayload): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- computed ----------------

    const opened = computed(() => vm.value?.opened ?? false)
    const confirmButtonLabel = computed(() => vm.value.confirmButtonLabel)
    const title = computed(() => vm.value.title)

    // writable computeds (replace getter + setter pairs)

    const name = computed<string>({
        get: () => vm.value.name,
        set: v => { vm.value.name = v }
    })

    const description = computed<string | null>({
        get: () => vm.value.description,
        set: v => { vm.value.description = v }
    })

    // ---------------- actions ----------------

    function handleFormSubmit()
    {
        emit(
            'form-submit',
            new RevenueEditorFormSubmitEventPayload(
                vm.value.id,
                name.value,
                description.value
            )
        )
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ title }}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <form id="revenue-editor-form" @submit.prevent="handleFormSubmit">
                            <div class="form-group">
                                <label>Name:</label>
                                <input type="text" v-model.trim="name" class="form-control" required minlength="2" maxlength="100" />
                            </div>
                            <div class="form-group">
                                <label>Description:</label>
                                <textarea v-model.trim="description" class="form-control" />
                            </div>
                        </form>
                    </div>
                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                        <button type="submit" form="revenue-editor-form" class="btn btn-primary">{{ confirmButtonLabel }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>